import { TWebsiteBuilderNormalizeTraverse } from "../../schema/normalization/websiteBuilderNormalizeEditor";
import { TWebsiteBuilderElement, websiteBuilderElementValid } from "../element/websiteBuilderElement";
import { IWebsiteBuilderNodeBase, IWebsiteBuilderNodeDesignStyles } from "../websiteBuilderNode";
import { IWebsiteBuilderNodeDimensions } from "../websiteBuilderNodeGeometry";
import { websiteBuilderNormalizeValue } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { TWebsiteBuilderDevice } from "../../website/shared/device";
import { IWebsiteBuilderTabset, WEBSITE_BUILDER_TABSET_HEADER_HEIGHT } from "../container/websiteBuilderTabset";

export interface IWebsiteBuilderTabSettings {
  title: string;
}

export interface IWebsiteBuilderTab extends IWebsiteBuilderNodeBase {
  type: "tab";
  settings: IWebsiteBuilderTabSettings;
  children: TWebsiteBuilderElement[];
}

export function isWebsiteBuilderTab(node: any): node is IWebsiteBuilderTab {
  return node?.type === "tab";
}

export function websiteBuilderTabValid(node: any): node is IWebsiteBuilderTab {
  const partialNode: Partial<IWebsiteBuilderTab> = node;

  return (
    partialNode != null &&
    partialNode.id != null &&
    partialNode.type === "tab"
  );
}

export function websiteBuilderGenerateTabDimensions(
  tabsetNode: IWebsiteBuilderTabset,
  device: TWebsiteBuilderDevice,
): IWebsiteBuilderNodeDimensions {
  const { width, height } = tabsetNode.container.layout[device].dimensions;

  return {
    width,
    height: Math.max(0, height - WEBSITE_BUILDER_TABSET_HEADER_HEIGHT),
  };
}

function generateDefaultTabSettings(title: string): IWebsiteBuilderTabSettings {
  return {
    title,
  };
}

export function websiteBuilderGenerateDefaultTab(title: string): IWebsiteBuilderTab {
  return {
    id: crypto.randomUUID(),
    type: "tab",
    settings: generateDefaultTabSettings(title),
    children: [],
  };
}

function websiteBuilderNormalizeTabSettings(partialSettings: Partial<IWebsiteBuilderTabSettings>): IWebsiteBuilderTabSettings {
  const defaultSettings = generateDefaultTabSettings("Tab"); // TODO: Translation

  if (partialSettings == null) {
    return defaultSettings;
  }

  const { title } = partialSettings;

  return {
    title: websiteBuilderNormalizeValue({
      type: "string",
      value: title,
      defaultValue: defaultSettings.title,
      required: true,
      validate: value => value.trim().length > 0,
    }),
  };
}

export function websiteBuilderNormalizeValidTab(
  node: IWebsiteBuilderTab,
  normalizeTraverse: TWebsiteBuilderNormalizeTraverse,
): IWebsiteBuilderTab {
  const children = node.children?.filter(child => {
    return websiteBuilderElementValid(child);
  }) ?? [];

  const normalizedSettings = websiteBuilderNormalizeTabSettings(node.settings);

  const normalizedTab: IWebsiteBuilderTab = {
    id: node.id,
    type: node.type,
    settings: normalizedSettings,
    children: children.map(child => normalizeTraverse(child)),
  };

  return normalizedTab;
}

export function websiteBuilderGenerateTabDesignStyles(): IWebsiteBuilderNodeDesignStyles {
  return {
    className: "website-builder-tab",
    styleProperties: {},
    styleDeclaration: "",
  };
}
